import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CheckDomainRes } from 'src/lib/fliko/models/http/ssoRequest.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { AuthService } from 'src/app/shared/services/auth.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';

@Component({
  selector: 'app-access-pending[email][ssoConfig]',
  styleUrls: ['./access-pending.component.scss'],
  templateUrl: './access-pending.component.html'
})
export class AccessPendingComponent implements OnInit {
  @Input() email!: string;
  @Input() ssoConfig: CheckDomainRes | null = null;
  @Output() onRequest = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  public isRequestingAccess = false;
  public accessRequestSent = false;

  public ssoDisplayNameCtrl = new FormControl('');

  constructor(
    private auth: AuthService,
    private ls: LocalizationService,
    private errorHandler: ErrorHandlingService
  ) {}

  ngOnInit(): void {}

  /**
   * Request SSO access - adds user to pending list
   * Called when user clicks "Request Access" button
   */
  public async requestAccess(): Promise<void> {
    if (this.isRequestingAccess || this.accessRequestSent) return;

    this.isRequestingAccess = true;

    try {
      // Get current user from Firebase Auth
      const currentAuthUser = await AsyncHlp.onFirst(this.auth.getCurrentUser$());
      if (!currentAuthUser) throw new Error('User not authenticated');

      const companyId = this.ssoConfig?.companyId;
      if (!companyId) throw new Error('Missing company configuration');

      // Request SSO access via Cloud Function
      const requestResponse = await this.auth.requestSsoAccess({
        email: this.email,
        companyId,
        displayName: this.ssoDisplayNameCtrl.value ?? ''
      });

      if (requestResponse) {
        this.accessRequestSent = true;
        this.onRequest.emit();

        // Log out user after sending request
        await this.auth.logOut();
      } else {
        throw new Error('Failed to submit SSO access request');
      }
    } catch (error) {
      this.errorHandler.showGlobalError(this.ls.translate('error.ssoAccessRequest'), error);
    } finally {
      this.isRequestingAccess = false;
    }
  }

  public backToEmail(): void {
    this.accessRequestSent = false;
    this.isRequestingAccess = false;
    this.onCancel.emit();
  }

  public getButtonSuffix(): string {
    return this.accessRequestSent ? 'sent' : this.isRequestingAccess ? 'sending' : 'button';
  }
}
